import { useLayoutEffect, useRef } from "react";
import { Trash2 } from "lucide-react";
import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";

/**
 * Issue #1221 slice 2 (D223.1) — the one in-page way to confirm a delete,
 * with the words as props. `DeleteMatchDialog.jsx` is its shape and its
 * reasoning (the kit's red alert dialog: a real modal with focus inside,
 * Escape is Keep it, a failed delete stays on the dialog so the failure has
 * somewhere to appear, D57); this takes the title, the two labels and the
 * test ids from the caller so the coach's goals, feedback notes and private
 * notes (slices 2 and 3) share one dialog and not three.
 *
 * `testId` is a prefix: the dialog is `<testId>-dialog`, Keep it is
 * `<testId>-cancel` and Delete is `<testId>-confirm` (the board's
 * `cp-goal-delete-dialog` / `-confirm`). Delete is a plain button, not
 * `AlertDialogPrimitive.Action`, which would close the dialog on press.
 *
 * Focus returns to the control that opened it; if that control is gone (the
 * card was just deleted) to the element marked `data-focus-after-delete`.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {string} props.title
 * @param {string} [props.description]
 * @param {string} [props.keepLabel]
 * @param {string} props.confirmLabel
 * @param {boolean} [props.deleting]
 * @param {string|null} [props.error]
 * @param {string} props.testId
 * @param {() => void} props.onCancel
 * @param {() => void} props.onConfirm
 */
export default function ConfirmDeleteDialog({
  open,
  title,
  description = null,
  keepLabel = "Keep it",
  confirmLabel,
  deleting = false,
  error = null,
  testId,
  onCancel,
  onConfirm,
}) {
  const opener = useRef(null);
  useLayoutEffect(() => {
    if (open) opener.current = document.activeElement;
  }, [open]);
  return (
    <AlertDialogPrimitive.Root
      open={Boolean(open)}
      onOpenChange={(next) => {
        if (!next && !deleting) onCancel();
      }}
    >
      {open && (
        <AlertDialogPrimitive.Portal>
          <AlertDialogPrimitive.Overlay
            className="nw-alert-fade"
            style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.4)", zIndex: 50 }}
          />
          <AlertDialogPrimitive.Content
            className="nw-alert-fade"
            data-testid={`${testId}-dialog`}
            aria-modal="true"
            {...(description ? {} : { "aria-describedby": undefined })}
            onCloseAutoFocus={(e) => {
              e.preventDefault();
              const el = opener.current;
              opener.current = null;
              const back = el && el.isConnected ? el : document.querySelector("[data-focus-after-delete]");
              if (back && typeof back.focus === "function") back.focus();
            }}
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 50,
              display: "flex",
              flexDirection: "column",
              gap: 12,
              padding: 18,
              background: "var(--nw-surface)",
              border: "1px solid var(--nw-line-strong)",
              borderRadius: 14,
              boxShadow: "0 8px 24px rgba(15,23,42,0.12)",
              maxWidth: 360,
              width: "calc(100% - 40px)",
            }}
          >
            <AlertDialogPrimitive.Title
              style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: 17, lineHeight: "normal", color: "var(--nw-ink)", overflowWrap: "anywhere" }}
            >
              {title}
            </AlertDialogPrimitive.Title>
            {description && (
              <AlertDialogPrimitive.Description className="nw-prose" style={{ color: "inherit" }}>
                {description}
              </AlertDialogPrimitive.Description>
            )}
            {error && (
              <div className="nw-help" role="alert" style={{ color: "var(--nw-ink)" }}>
                {error}
              </div>
            )}
            <div style={{ display: "flex", gap: 10 }}>
              <AlertDialogPrimitive.Cancel
                data-testid={`${testId}-cancel`}
                disabled={deleting}
                style={{
                  display: "flex",
                  flex: 1,
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                  minHeight: 48,
                  margin: 0,
                  background: "var(--nw-surface)",
                  border: "1px solid var(--nw-line-strong)",
                  color: "var(--nw-navy)",
                  fontWeight: 600,
                  fontSize: 14,
                  borderRadius: "var(--nw-radius-control)",
                  opacity: deleting ? 0.7 : 1,
                }}
              >
                {keepLabel}
              </AlertDialogPrimitive.Cancel>
              <button
                type="button"
                data-testid={`${testId}-confirm`}
                onClick={onConfirm}
                disabled={deleting}
                style={{
                  display: "flex",
                  flex: 1,
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  minHeight: 48,
                  background: "var(--nw-alert)",
                  color: "#FFFFFF",
                  fontWeight: 700,
                  fontSize: 15,
                  borderRadius: "var(--nw-radius-control)",
                  border: "none",
                  opacity: deleting ? 0.7 : 1,
                  cursor: deleting ? "not-allowed" : "pointer",
                }}
              >
                <Trash2 size={16} aria-hidden="true" />
                {confirmLabel}
              </button>
            </div>
          </AlertDialogPrimitive.Content>
        </AlertDialogPrimitive.Portal>
      )}
    </AlertDialogPrimitive.Root>
  );
}
